import { Schema } from "effect";
import {
  OfficeBoundaryParseOptions,
  OfficeCanonicalIdSchema,
  OfficeDocumentPatchEnvelopeSchema,
  OfficeEngineSchema,
  OfficeMarkdownPatchEnvelopeSchema,
  OfficeNativeIdentityAliasSchema,
  OfficeNativeMediaTypeSchema,
  OfficePdfPatchEnvelopeSchema,
  OfficePresentationPatchEnvelopeSchema,
  OfficePublicErrorSchema,
  OfficeSpreadsheetPatchEnvelopeSchema,
} from "./office";
import {
  OfficeArtifactIdSchema,
  OfficeArtifactRevisionIdSchema,
  OfficeStorageObjectIdSchema,
} from "./office-content";
import {
  ProjectFileActorUserIdSchema,
  ProjectFileIsoDateTimeSchema,
  ProjectFilePositiveIntegerSchema,
  ProjectFilePrivateObjectKeySchema,
  ProjectFileSha256Schema,
} from "./project-files";
import {
  SandboxProjectIdSchema,
  SandboxRequestIdSchema,
  SandboxWorkspaceIdSchema,
} from "./sandbox";
import { toPapersFlowStandardSchema } from "./standard";

export const OFFICE_REVISION_SAVE_MAX_BYTES = 50 * 1_048_576;
export const OFFICE_REVISION_SAVE_MAX_OPERATIONS = 64;
export const OFFICE_REVISION_SAVE_MAX_REQUEST_BYTES = 512 * 1_024;
export const OFFICE_REVISION_SAVE_INTERNAL_MAX_BYTES =
  OFFICE_REVISION_SAVE_MAX_REQUEST_BYTES + 16 * 1_024;
export const OFFICE_PATCH_LEASE_MS = 30_000;
export const OFFICE_PATCH_HEARTBEAT_MS = 5_000;
export const OFFICE_PATCH_WAIT_TIMEOUT_MS = 5 * 60_000;
export const OFFICE_AGENT_PATCH_OPERATION_ID_MAX_LENGTH = 333;

export const OfficePatchOperationIdSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(OFFICE_AGENT_PATCH_OPERATION_ID_MAX_LENGTH),
  Schema.makeFilter(
    (value) =>
      value === value.trim() &&
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return codePoint !== undefined && codePoint > 31 && codePoint !== 127;
      }),
    { expected: "a canonical Office patch operation identifier" },
  ),
).pipe(Schema.brand("OfficePatchOperationId"));

export const OfficePatchIdSchema = OfficeCanonicalIdSchema.pipe(Schema.brand("OfficePatchId"));
export const OfficePatchWorkerIdSchema = OfficeCanonicalIdSchema.pipe(
  Schema.brand("OfficePatchWorkerId"),
);

export const OfficeRevisionSaveResultSchema = Schema.Struct({
  operationId: OfficePatchOperationIdSchema,
  artifactId: OfficeArtifactIdSchema,
  revisionId: OfficeArtifactRevisionIdSchema,
  revision: ProjectFilePositiveIntegerSchema,
  status: Schema.Literal("ready"),
});

const OfficeUserPatchAuthorSchema = Schema.Struct({
  type: Schema.Literal("user"),
  id: OfficeCanonicalIdSchema,
});
const OfficeAgentPatchAuthorSchema = Schema.Struct({
  type: Schema.Literal("agent"),
  id: OfficeCanonicalIdSchema,
  runId: OfficeCanonicalIdSchema,
});

export const OfficeUserAuthoredPatchEnvelopeSchema = Schema.Union([
  Schema.Struct({
    ...OfficeDocumentPatchEnvelopeSchema.fields,
    author: OfficeUserPatchAuthorSchema,
  }),
  Schema.Struct({
    ...OfficePresentationPatchEnvelopeSchema.fields,
    author: OfficeUserPatchAuthorSchema,
  }),
  Schema.Struct({
    ...OfficeSpreadsheetPatchEnvelopeSchema.fields,
    author: OfficeUserPatchAuthorSchema,
  }),
  Schema.Struct({
    ...OfficeMarkdownPatchEnvelopeSchema.fields,
    author: OfficeUserPatchAuthorSchema,
  }),
  Schema.Struct({
    ...OfficePdfPatchEnvelopeSchema.fields,
    author: OfficeUserPatchAuthorSchema,
  }),
]);

/** Public browser/Agent API input. The authenticated user actor is resolved separately. */
export const OfficeRevisionSaveRequestSchema = OfficeUserAuthoredPatchEnvelopeSchema;

const AgentPatchEnvelopeOverrides = {
  operationId: OfficePatchOperationIdSchema,
  threadId: OfficeCanonicalIdSchema,
  author: OfficeAgentPatchAuthorSchema,
} as const;

export const OfficeAgentAuthoredPatchEnvelopeSchema = Schema.Union([
  Schema.Struct({
    ...OfficeDocumentPatchEnvelopeSchema.fields,
    ...AgentPatchEnvelopeOverrides,
  }),
  Schema.Struct({
    ...OfficePresentationPatchEnvelopeSchema.fields,
    ...AgentPatchEnvelopeOverrides,
  }),
  Schema.Struct({
    ...OfficeSpreadsheetPatchEnvelopeSchema.fields,
    ...AgentPatchEnvelopeOverrides,
  }),
  Schema.Struct({
    ...OfficeMarkdownPatchEnvelopeSchema.fields,
    ...AgentPatchEnvelopeOverrides,
  }),
  Schema.Struct({
    ...OfficePdfPatchEnvelopeSchema.fields,
    ...AgentPatchEnvelopeOverrides,
  }),
]);

export const OfficeDurablePatchEnvelopeSchema = Schema.Union([
  OfficeUserAuthoredPatchEnvelopeSchema,
  OfficeAgentAuthoredPatchEnvelopeSchema,
]);

const OfficeAgentPatchAttemptGenerationSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const OfficeAgentPatchAuthorityFields = {
  runId: OfficeCanonicalIdSchema,
  attemptId: OfficeCanonicalIdSchema,
  attemptGeneration: OfficeAgentPatchAttemptGenerationSchema,
  agentId: OfficeCanonicalIdSchema,
  toolCallId: OfficeCanonicalIdSchema,
} as const;

export const OfficeRepositoryBeginUserPatchRequestSchema = Schema.Struct({
  actorUserId: ProjectFileActorUserIdSchema,
  patch: OfficeUserAuthoredPatchEnvelopeSchema,
});

export const OfficeRepositoryBeginAgentPatchRequestSchema = Schema.Struct({
  actorUserId: ProjectFileActorUserIdSchema,
  ...OfficeAgentPatchAuthorityFields,
  patch: OfficeAgentAuthoredPatchEnvelopeSchema,
}).check(
  Schema.makeFilter(
    (request) =>
      (request.patch.author.id === request.agentId &&
        request.patch.author.runId === request.runId &&
        request.patch.operationId === `office-edit:${request.attemptId}:${request.toolCallId}`) ||
      "agent Office patch authority must match its author and operation identity",
  ),
);

export const OfficeRepositoryBeginPatchRequestSchema = Schema.Union([
  OfficeRepositoryBeginUserPatchRequestSchema,
  OfficeRepositoryBeginAgentPatchRequestSchema,
]);

export const OfficeRepositoryBeginPatchResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("pending"),
    patchId: OfficePatchIdSchema,
    artifactId: OfficeArtifactIdSchema,
    revisionId: OfficeArtifactRevisionIdSchema,
    revision: ProjectFilePositiveIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    patchId: OfficePatchIdSchema,
    result: OfficeRevisionSaveResultSchema,
  }),
]);

const OfficePatchScopeFields = {
  requestId: SandboxRequestIdSchema,
  actorUserId: ProjectFileActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  patchId: OfficePatchIdSchema,
} as const;

export const OfficeRepositoryUserPatchScopeSchema = Schema.Struct(OfficePatchScopeFields);
export const OfficeRepositoryAgentPatchScopeSchema = Schema.Struct({
  ...OfficePatchScopeFields,
  ...OfficeAgentPatchAuthorityFields,
});
export const OfficeRepositoryGetPatchRequestSchema = Schema.Union([
  OfficeRepositoryAgentPatchScopeSchema,
  OfficeRepositoryUserPatchScopeSchema,
]);
export const OfficeRepositoryCancelPatchRequestSchema = Schema.Union([
  OfficeRepositoryAgentPatchScopeSchema,
  OfficeRepositoryUserPatchScopeSchema,
]);

export const OfficeRepositoryPatchStatusSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literals(["pending", "processing"]),
    operationId: OfficePatchOperationIdSchema,
    artifactId: OfficeArtifactIdSchema,
    revisionId: OfficeArtifactRevisionIdSchema,
    revision: ProjectFilePositiveIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    result: OfficeRevisionSaveResultSchema,
  }),
  Schema.Struct({
    status: Schema.Literals(["failed", "cancelled"]),
    operationId: OfficePatchOperationIdSchema,
    error: OfficePublicErrorSchema,
  }),
]);

export const OfficeRepositoryCancelPatchResultSchema = Schema.Struct({
  status: Schema.Literals(["ready", "failed", "cancelled"]),
});

/** The Agent API gives the Worker only a durable pointer, never native bytes or patch content. */
export const OfficePatchPointerSchema = Schema.Struct({
  schemaVersion: Schema.Literal(1),
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  patchId: OfficePatchIdSchema,
});

export const OfficePatchStartResultSchema = Schema.Struct({
  accepted: Schema.Literal(true),
  patchId: OfficePatchIdSchema,
});

const OfficeWorkerPatchScopeFields = {
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  patchId: OfficePatchIdSchema,
} as const;

export const OfficeClaimPatchRequestSchema = Schema.Struct({
  ...OfficeWorkerPatchScopeFields,
  workerId: OfficePatchWorkerIdSchema,
  leaseDurationMs: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(5_000),
    Schema.isLessThanOrEqualTo(120_000),
  ),
});

export const OfficePatchLeaseSchema = Schema.Struct({
  ...OfficeWorkerPatchScopeFields,
  workerId: OfficePatchWorkerIdSchema,
  leaseGeneration: ProjectFilePositiveIntegerSchema,
  cancellationGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
});

export const OfficeClaimedPatchSchema = Schema.Struct({
  lease: OfficePatchLeaseSchema,
  patch: OfficeDurablePatchEnvelopeSchema,
  source: Schema.Struct({
    revisionId: OfficeArtifactRevisionIdSchema,
    storageObjectId: OfficeStorageObjectIdSchema,
    objectKey: ProjectFilePrivateObjectKeySchema,
    contentType: OfficeNativeMediaTypeSchema,
    byteSize: ProjectFilePositiveIntegerSchema.check(
      Schema.isLessThanOrEqualTo(OFFICE_REVISION_SAVE_MAX_BYTES),
    ),
    sha256: ProjectFileSha256Schema,
  }),
  output: Schema.Struct({
    revisionId: OfficeArtifactRevisionIdSchema,
    storageObjectId: OfficeStorageObjectIdSchema,
    objectKey: ProjectFilePrivateObjectKeySchema,
    contentType: OfficeNativeMediaTypeSchema,
    maxByteSize: Schema.Literal(OFFICE_REVISION_SAVE_MAX_BYTES),
  }),
});

export const OfficeClaimPatchResultSchema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("claimed"), claim: OfficeClaimedPatchSchema }),
  Schema.Struct({ status: Schema.Literal("rejected"), error: OfficePublicErrorSchema }),
]);

export const OfficeRenewPatchRequestSchema = Schema.Struct({
  lease: OfficePatchLeaseSchema,
  leaseDurationMs: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(5_000),
    Schema.isLessThanOrEqualTo(120_000),
  ),
});

export const OfficeRenewPatchResultSchema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("renewed") }),
  Schema.Struct({ status: Schema.Literal("rejected"), error: OfficePublicErrorSchema }),
]);

export const OfficeCompletePatchRequestSchema = Schema.Struct({
  lease: OfficePatchLeaseSchema,
  output: Schema.Struct({
    objectKey: ProjectFilePrivateObjectKeySchema,
    contentType: OfficeNativeMediaTypeSchema,
    byteSize: ProjectFilePositiveIntegerSchema.check(
      Schema.isLessThanOrEqualTo(OFFICE_REVISION_SAVE_MAX_BYTES),
    ),
    sha256: ProjectFileSha256Schema,
    etag: OfficeCanonicalIdSchema.check(Schema.isMaxLength(512)),
  }),
  engine: OfficeEngineSchema,
  engineVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
  operationCount: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(OFFICE_REVISION_SAVE_MAX_OPERATIONS),
  ),
  identityAliases: Schema.Array(OfficeNativeIdentityAliasSchema).check(Schema.isMaxLength(1_024)),
});

export const OfficeCompletePatchResultSchema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("ready"), result: OfficeRevisionSaveResultSchema }),
  Schema.Struct({ status: Schema.Literal("rejected"), error: OfficePublicErrorSchema }),
]);

export const OfficeFailPatchRequestSchema = Schema.Struct({
  lease: OfficePatchLeaseSchema,
  error: OfficePublicErrorSchema,
});

export const OfficeFailPatchResultSchema = Schema.Struct({ status: Schema.Literal("failed") });

export const OfficeAuthorizePatchCleanupRequestSchema = Schema.Struct({
  ...OfficeWorkerPatchScopeFields,
  objectKey: ProjectFilePrivateObjectKeySchema,
});

export const OfficeAuthorizePatchCleanupResultSchema = Schema.Struct({
  status: Schema.Literal("authorized"),
});

export const OfficeRecordPatchCleanupRequestSchema = Schema.Struct({
  ...OfficeWorkerPatchScopeFields,
  objectKey: ProjectFilePrivateObjectKeySchema,
  outcome: Schema.Literals(["deleted", "missing"]),
});

export const OfficeRecordPatchCleanupResultSchema = Schema.Struct({
  status: Schema.Literal("deleted"),
});

export const OfficeProcessorRequestSchema = Schema.Struct({
  schemaVersion: Schema.Literal(1),
  patch: OfficeDurablePatchEnvelopeSchema,
  sourceByteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_REVISION_SAVE_MAX_BYTES),
  ),
  sourceSha256: ProjectFileSha256Schema,
  sourceFileName: Schema.Literals([
    "source.docx",
    "source.pptx",
    "source.xlsx",
    "source.pdf",
    "source.md",
  ]),
  outputFileName: Schema.Literals([
    "output.docx",
    "output.pptx",
    "output.xlsx",
    "output.pdf",
    "output.md",
  ]),
  maxOutputBytes: Schema.Literal(OFFICE_REVISION_SAVE_MAX_BYTES),
});

export const OfficeProcessorResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("ready"),
    engine: OfficeEngineSchema,
    engineVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
    contentType: OfficeNativeMediaTypeSchema,
    byteSize: ProjectFilePositiveIntegerSchema.check(
      Schema.isLessThanOrEqualTo(OFFICE_REVISION_SAVE_MAX_BYTES),
    ),
    outputSha256: ProjectFileSha256Schema,
    operationCount: Schema.Int.check(
      Schema.isGreaterThanOrEqualTo(1),
      Schema.isLessThanOrEqualTo(OFFICE_REVISION_SAVE_MAX_OPERATIONS),
    ),
    identityAliases: Schema.Array(OfficeNativeIdentityAliasSchema).check(Schema.isMaxLength(1_024)),
  }),
  Schema.Struct({
    status: Schema.Literal("failed"),
    error: OfficePublicErrorSchema,
  }),
]);

export const OfficeAgentEditInputSchema = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("document"),
    summary: OfficeDocumentPatchEnvelopeSchema.fields.summary,
    operations: OfficeDocumentPatchEnvelopeSchema.fields.operations,
  }),
  Schema.Struct({
    kind: Schema.Literal("presentation"),
    summary: OfficePresentationPatchEnvelopeSchema.fields.summary,
    operations: OfficePresentationPatchEnvelopeSchema.fields.operations,
  }),
  Schema.Struct({
    kind: Schema.Literal("spreadsheet"),
    summary: OfficeSpreadsheetPatchEnvelopeSchema.fields.summary,
    operations: OfficeSpreadsheetPatchEnvelopeSchema.fields.operations,
  }),
  Schema.Struct({
    kind: Schema.Literal("markdown"),
    summary: OfficeMarkdownPatchEnvelopeSchema.fields.summary,
    operations: OfficeMarkdownPatchEnvelopeSchema.fields.operations,
  }),
]);
export const OfficeAgentEditInputStandardSchema = toPapersFlowStandardSchema(
  OfficeAgentEditInputSchema,
);

export const OfficeAgentPdfEditInputSchema = Schema.Struct({
  kind: Schema.Literal("pdf"),
  summary: OfficePdfPatchEnvelopeSchema.fields.summary,
  operations: OfficePdfPatchEnvelopeSchema.fields.operations,
});
export const OfficeAgentPdfEditInputStandardSchema = toPapersFlowStandardSchema(
  OfficeAgentPdfEditInputSchema,
);

export type OfficeRevisionSaveResult = typeof OfficeRevisionSaveResultSchema.Type;
export type OfficeRevisionSaveResultEncoded = typeof OfficeRevisionSaveResultSchema.Encoded;
export type OfficeRevisionSaveRequest = typeof OfficeRevisionSaveRequestSchema.Type;
export type OfficeRevisionSaveRequestEncoded = typeof OfficeRevisionSaveRequestSchema.Encoded;
export type OfficePatchOperationId = typeof OfficePatchOperationIdSchema.Type;
export type OfficeUserAuthoredPatchEnvelope = typeof OfficeUserAuthoredPatchEnvelopeSchema.Type;
export type OfficeUserAuthoredPatchEnvelopeEncoded =
  typeof OfficeUserAuthoredPatchEnvelopeSchema.Encoded;
export type OfficeAgentAuthoredPatchEnvelope = typeof OfficeAgentAuthoredPatchEnvelopeSchema.Type;
export type OfficeAgentAuthoredPatchEnvelopeEncoded =
  typeof OfficeAgentAuthoredPatchEnvelopeSchema.Encoded;
export type OfficeDurablePatchEnvelope = typeof OfficeDurablePatchEnvelopeSchema.Type;
export type OfficeDurablePatchEnvelopeEncoded = typeof OfficeDurablePatchEnvelopeSchema.Encoded;
export type OfficeRepositoryBeginPatchRequest = typeof OfficeRepositoryBeginPatchRequestSchema.Type;
export type OfficeRepositoryBeginPatchRequestEncoded =
  typeof OfficeRepositoryBeginPatchRequestSchema.Encoded;
export type OfficeRepositoryBeginUserPatchRequest =
  typeof OfficeRepositoryBeginUserPatchRequestSchema.Type;
export type OfficeRepositoryBeginAgentPatchRequest =
  typeof OfficeRepositoryBeginAgentPatchRequestSchema.Type;
export type OfficeRepositoryBeginPatchResult = typeof OfficeRepositoryBeginPatchResultSchema.Type;
export type OfficeRepositoryGetPatchRequest = typeof OfficeRepositoryGetPatchRequestSchema.Type;
export type OfficeRepositoryGetPatchRequestEncoded =
  typeof OfficeRepositoryGetPatchRequestSchema.Encoded;
export type OfficeRepositoryCancelPatchRequest =
  typeof OfficeRepositoryCancelPatchRequestSchema.Type;
export type OfficeRepositoryCancelPatchRequestEncoded =
  typeof OfficeRepositoryCancelPatchRequestSchema.Encoded;
export type OfficeRepositoryAgentPatchScope = typeof OfficeRepositoryAgentPatchScopeSchema.Type;
export type OfficeRepositoryPatchStatus = typeof OfficeRepositoryPatchStatusSchema.Type;
export type OfficeRepositoryCancelPatchResult = typeof OfficeRepositoryCancelPatchResultSchema.Type;
export type OfficePatchPointer = typeof OfficePatchPointerSchema.Type;
export type OfficePatchPointerEncoded = typeof OfficePatchPointerSchema.Encoded;
export type OfficePatchStartResult = typeof OfficePatchStartResultSchema.Type;
export type OfficeClaimPatchRequest = typeof OfficeClaimPatchRequestSchema.Type;
export type OfficePatchLease = typeof OfficePatchLeaseSchema.Type;
export type OfficeClaimedPatch = typeof OfficeClaimedPatchSchema.Type;
export type OfficeClaimPatchResult = typeof OfficeClaimPatchResultSchema.Type;
export type OfficeRenewPatchRequest = typeof OfficeRenewPatchRequestSchema.Type;
export type OfficeRenewPatchResult = typeof OfficeRenewPatchResultSchema.Type;
export type OfficeCompletePatchRequest = typeof OfficeCompletePatchRequestSchema.Type;
export type OfficeCompletePatchResult = typeof OfficeCompletePatchResultSchema.Type;
export type OfficeFailPatchRequest = typeof OfficeFailPatchRequestSchema.Type;
export type OfficeFailPatchResult = typeof OfficeFailPatchResultSchema.Type;
export type OfficeAuthorizePatchCleanupRequest =
  typeof OfficeAuthorizePatchCleanupRequestSchema.Type;
export type OfficeAuthorizePatchCleanupResult = typeof OfficeAuthorizePatchCleanupResultSchema.Type;
export type OfficeRecordPatchCleanupRequest = typeof OfficeRecordPatchCleanupRequestSchema.Type;
export type OfficeRecordPatchCleanupResult = typeof OfficeRecordPatchCleanupResultSchema.Type;
export type OfficeProcessorRequest = typeof OfficeProcessorRequestSchema.Type;
export type OfficeProcessorResult = typeof OfficeProcessorResultSchema.Type;
export type OfficeAgentEditInput = typeof OfficeAgentEditInputSchema.Type;
export type OfficeAgentEditInputEncoded = typeof OfficeAgentEditInputSchema.Encoded;
export type OfficeAgentPdfEditInput = typeof OfficeAgentPdfEditInputSchema.Type;
export type OfficeAgentPdfEditInputEncoded = typeof OfficeAgentPdfEditInputSchema.Encoded;

const decode = <A, I>(schema: Schema.Codec<A, I>) =>
  Schema.decodeUnknownSync(schema, OfficeBoundaryParseOptions);

export const decodeOfficeRevisionSaveResult = decode(OfficeRevisionSaveResultSchema);
export const decodeOfficeRevisionSaveRequest = decode(OfficeRevisionSaveRequestSchema);
export const decodeOfficePatchOperationId = decode(OfficePatchOperationIdSchema);
export const decodeOfficeAgentAuthoredPatchEnvelope = decode(
  OfficeAgentAuthoredPatchEnvelopeSchema,
);
export const decodeOfficeDurablePatchEnvelope = decode(OfficeDurablePatchEnvelopeSchema);
export const decodeOfficeRepositoryBeginPatchRequest = decode(
  OfficeRepositoryBeginPatchRequestSchema,
);
export const decodeOfficeRepositoryBeginPatchResult = decode(
  OfficeRepositoryBeginPatchResultSchema,
);
export const decodeOfficeRepositoryGetPatchRequest = decode(OfficeRepositoryGetPatchRequestSchema);
export const decodeOfficeRepositoryCancelPatchRequest = decode(
  OfficeRepositoryCancelPatchRequestSchema,
);
export const decodeOfficeRepositoryPatchStatus = decode(OfficeRepositoryPatchStatusSchema);
export const decodeOfficeRepositoryCancelPatchResult = decode(
  OfficeRepositoryCancelPatchResultSchema,
);
export const decodeOfficePatchPointer = decode(OfficePatchPointerSchema);
export const decodeOfficePatchStartResult = decode(OfficePatchStartResultSchema);
export const decodeOfficeClaimPatchRequest = decode(OfficeClaimPatchRequestSchema);
export const decodeOfficeClaimedPatch = decode(OfficeClaimedPatchSchema);
export const decodeOfficeClaimPatchResult = decode(OfficeClaimPatchResultSchema);
export const decodeOfficeRenewPatchRequest = decode(OfficeRenewPatchRequestSchema);
export const decodeOfficeRenewPatchResult = decode(OfficeRenewPatchResultSchema);
export const decodeOfficeCompletePatchRequest = decode(OfficeCompletePatchRequestSchema);
export const decodeOfficeCompletePatchResult = decode(OfficeCompletePatchResultSchema);
export const decodeOfficeFailPatchRequest = decode(OfficeFailPatchRequestSchema);
export const decodeOfficeFailPatchResult = decode(OfficeFailPatchResultSchema);
export const decodeOfficeAuthorizePatchCleanupRequest = decode(
  OfficeAuthorizePatchCleanupRequestSchema,
);
export const decodeOfficeAuthorizePatchCleanupResult = decode(
  OfficeAuthorizePatchCleanupResultSchema,
);
export const decodeOfficeRecordPatchCleanupRequest = decode(OfficeRecordPatchCleanupRequestSchema);
export const decodeOfficeRecordPatchCleanupResult = decode(OfficeRecordPatchCleanupResultSchema);
export const decodeOfficeProcessorRequest = decode(OfficeProcessorRequestSchema);
export const decodeOfficeProcessorResult = decode(OfficeProcessorResultSchema);
export const decodeOfficeAgentEditInput = decode(OfficeAgentEditInputSchema);
export const decodeOfficeAgentPdfEditInput = decode(OfficeAgentPdfEditInputSchema);
export const decodeOfficeSavePublicError = decode(OfficePublicErrorSchema);

export const encodeOfficeRepositoryBeginPatchRequest = Schema.encodeSync(
  OfficeRepositoryBeginPatchRequestSchema,
);
export const encodeOfficeAgentAuthoredPatchEnvelope = Schema.encodeSync(
  OfficeAgentAuthoredPatchEnvelopeSchema,
);
export const encodeOfficeDurablePatchEnvelope = Schema.encodeSync(OfficeDurablePatchEnvelopeSchema);
export const encodeOfficeRevisionSaveRequest = Schema.encodeSync(OfficeRevisionSaveRequestSchema);
export const encodeOfficeRevisionSaveResult = Schema.encodeSync(OfficeRevisionSaveResultSchema);
export const encodeOfficeRepositoryGetPatchRequest = Schema.encodeSync(
  OfficeRepositoryGetPatchRequestSchema,
);
export const encodeOfficeRepositoryCancelPatchRequest = Schema.encodeSync(
  OfficeRepositoryCancelPatchRequestSchema,
);
export const encodeOfficePatchPointer = Schema.encodeSync(OfficePatchPointerSchema);
export const encodeOfficeClaimPatchRequest = Schema.encodeSync(OfficeClaimPatchRequestSchema);
export const encodeOfficeRenewPatchRequest = Schema.encodeSync(OfficeRenewPatchRequestSchema);
export const encodeOfficeCompletePatchRequest = Schema.encodeSync(OfficeCompletePatchRequestSchema);
export const encodeOfficeFailPatchRequest = Schema.encodeSync(OfficeFailPatchRequestSchema);
export const encodeOfficeAuthorizePatchCleanupRequest = Schema.encodeSync(
  OfficeAuthorizePatchCleanupRequestSchema,
);
export const encodeOfficeRecordPatchCleanupRequest = Schema.encodeSync(
  OfficeRecordPatchCleanupRequestSchema,
);
export const encodeOfficeProcessorRequest = Schema.encodeSync(OfficeProcessorRequestSchema);
export const encodeOfficeProcessorResult = Schema.encodeSync(OfficeProcessorResultSchema);
export const encodeOfficeAgentEditInput = Schema.encodeSync(OfficeAgentEditInputSchema);
export const encodeOfficeAgentPdfEditInput = Schema.encodeSync(OfficeAgentPdfEditInputSchema);

export function formatOfficeAgentPatchOperationId(input: {
  readonly attemptId: string;
  readonly toolCallId: string;
}): OfficePatchOperationId {
  return decodeOfficePatchOperationId(`office-edit:${input.attemptId}:${input.toolCallId}`);
}

export const OfficePatchTimestampSchema = ProjectFileIsoDateTimeSchema;
